import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

const DATA_PAGE_PARAM = "dataPage";

/**
 * Owns the `?dataPage=` URL param used to track which tab is active on a
 * tabbed dataset page. Falls back to `defaultKey` whenever the current param
 * value isn't (yet) one of `validKeys` — e.g. before the page's tab list has
 * loaded — without erasing the param, so a valid-but-not-yet-known key isn't
 * lost out from under an in-flight page load.
 */
export function useDataPageTabSearchParam(
  validKeys: string[],
  defaultKey: string,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawKey = searchParams.get(DATA_PAGE_PARAM);

  const activeKey = useMemo(
    () => (rawKey && validKeys.includes(rawKey) ? rawKey : defaultKey),
    [rawKey, validKeys, defaultKey],
  );

  const setActiveKey = useCallback(
    (nextKey: string) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (nextKey === defaultKey) {
            next.delete(DATA_PAGE_PARAM);
          } else {
            next.set(DATA_PAGE_PARAM, nextKey);
          }
          return next;
        },
        { replace: true },
      );
    },
    [defaultKey, setSearchParams],
  );

  return { activeKey, setActiveKey };
}
